import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Snackbar,
  Switch,
  FormControlLabel,
  InputAdornment,
  Divider,
  Avatar,
  Checkbox,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  Edit,
  Delete,
  Add,
  Pool,
  FitnessCenter,
  Spa,
  Restaurant,
  BusinessCenter,
  LocalParking,
  MeetingRoom,
  Wifi,
  SportsTennis,
  Category,
  CheckCircle,
  Schedule,
  LocationOn,
  People,
  AttachMoney,
  Discount,
  Percent,
  LocalOffer,
  Block,
  Save,
  ClearAll,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../../components/TopNav_hotelStaff';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const FacilityManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedIds, setSelectedIds] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    requiresBooking: '',
    hasDiscount: '',
    search: '',
  });

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    location: '',
    capacity: '',
    operating_hours: '',
    maintenance_schedule: '',
    is_available: true,
    requires_booking: false,
    booking_fee: '',
    discount_percentage: '',
    discount_valid_from: '',
    discount_valid_to: '',
    image_url: '',
  });

  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDiscountDialogOpen, setBulkDiscountDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Bulk discount form
  const [bulkDiscountData, setBulkDiscountData] = useState({
    discount_percentage: '',
    discount_valid_from: '',
    discount_valid_to: '',
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Navigation and sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Category icons mapping
  const categoryIcons = {
    swimming_pool: <Pool />,
    gym: <FitnessCenter />,
    spa: <Spa />,
    restaurant: <Restaurant />,
    business_center: <BusinessCenter />,
    conference_room: <MeetingRoom />,
    parking: <LocalParking />,
    sports: <SportsTennis />,
    wifi: <Wifi />,
    other: <Category />,
  };

  const categoryNames = {
    swimming_pool: 'Swimming Pool',
    gym: 'Gym',
    spa: 'Spa',
    restaurant: 'Restaurant',
    business_center: 'Business Center',
    conference_room: 'Conference Room',
    parking: 'Parking',
    sports: 'Sports',
    wifi: 'WiFi',
    other: 'Other',
  };

  // Snackbar function
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    showSnackbar('You have new notifications!', 'info');
  };

  // Get hotel code and staff ID
  const getHotelCode = useCallback(() => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || 'eHT5678042024';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return 'eHT5678042024';
    }
  }, []);

  const getStaffId = useCallback(() => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.staff_id || storedProfile.id || null;
    } catch (error) {
      console.error('Error getting staff ID:', error);
      return null;
    }
  }, []);

  // Normalize facility data - FIXED
  const normalizeFacilityData = (facility) => {
    return {
      ...facility,
      is_available: facility.is_available === 1 || facility.is_available === true || facility.is_available === '1',
      requires_booking: facility.requires_booking === 1 || facility.requires_booking === true || facility.requires_booking === '1',
      discount_active: facility.discount_active === 1 || facility.discount_active === true || facility.discount_active === '1',
      booking_fee: parseFloat(facility.booking_fee) || 0,
      discount_percentage: parseFloat(facility.discount_percentage) || 0,
      savings: parseFloat(facility.savings) || 0,
      final_price: parseFloat(facility.final_price) || 0,
      capacity: facility.capacity ? parseInt(facility.capacity) : null,
    };
  };

  // API service functions
  const api = {
    getFacilities: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          params: { hotel_code: getHotelCode(), ...params }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching facilities:', error);
        return { success: false, facilities: [] };
      }
    },

    getFacility: async (id) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          params: { hotel_code: getHotelCode(), id }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching facility:', error);
        return { success: false };
      }
    },

    getCategories: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          params: { hotel_code: getHotelCode(), action: 'categories' }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching categories:', error);
        return { success: false, categories: [] };
      }
    },

    createFacility: async (data) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          ...data,
          hotel_code: getHotelCode(),
          created_by: getStaffId()
        });
        return response.data;
      } catch (error) {
        console.error('Error creating facility:', error);
        return { success: false, error: error.response?.data?.error || 'Failed to create facility' };
      }
    },

    updateFacility: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          ...data,
          id,
          hotel_code: getHotelCode()
        });
        return response.data;
      } catch (error) {
        console.error('Error updating facility:', error);
        return { success: false, error: error.response?.data?.error || 'Failed to update facility' };
      }
    },

    deleteFacility: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          params: { hotel_code: getHotelCode(), id }
        });
        return response.data;
      } catch (error) {
        console.error('Error deleting facility:', error);
        return { success: false, error: error.response?.data?.error || 'Failed to delete facility' };
      }
    },

    updateStatus: async (id, isAvailable) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          action: 'update_status',
          id,
          hotel_code: getHotelCode(),
          is_available: isAvailable ? 1 : 0
        });
        return response.data;
      } catch (error) {
        console.error('Error updating status:', error);
        return { success: false, error: 'Failed to update status' };
      }
    },

    bulkUpdateDiscount: async (facilityIds, discountData) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/facilities/create_facility.php`, {
          action: 'bulk_discount',
          hotel_code: getHotelCode(),
          facility_ids: facilityIds,
          ...discountData
        });
        return response.data;
      } catch (error) {
        console.error('Error bulk updating discounts:', error);
        return { success: false, error: 'Failed to update discounts' };
      }
    }
  };

  // Load initial data
  useEffect(() => {
    loadData();
    loadCategories();
  }, []);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [filters, facilities]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.getFacilities();
      if (result.success) {
        const normalizedFacilities = (result.facilities || []).map(normalizeFacilityData);
        setFacilities(normalizedFacilities);
      } else {
        showSnackbar(result.error || 'Failed to load facilities', 'error');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load facilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const result = await api.getCategories();
      if (result.success) {
        setCategories(result.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...facilities];

    if (filters.category) {
      filtered = filtered.filter(f => f.category === filters.category);
    }

    if (filters.status) {
      if (filters.status === 'available') {
        filtered = filtered.filter(f => f.is_available === true);
      } else if (filters.status === 'unavailable') {
        filtered = filtered.filter(f => f.is_available === false);
      }
    }

    if (filters.requiresBooking !== '') {
      filtered = filtered.filter(f => f.requires_booking === (filters.requiresBooking === 'true'));
    }

    if (filters.hasDiscount) {
      if (filters.hasDiscount === 'active') {
        filtered = filtered.filter(f => f.discount_active === true);
      } else if (filters.hasDiscount === 'inactive') {
        filtered = filtered.filter(f => !f.discount_active && f.discount_percentage > 0);
      } else if (filters.hasDiscount === 'none') {
        filtered = filtered.filter(f => !f.discount_percentage || f.discount_percentage === 0);
      }
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(f => 
        f.name.toLowerCase().includes(searchLower) ||
        (f.description && f.description.toLowerCase().includes(searchLower)) ||
        (f.location && f.location.toLowerCase().includes(searchLower))
      );
    }

    setFilteredFacilities(filtered);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      status: '',
      requiresBooking: '',
      hasDiscount: '',
      search: '',
    });
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Facility name is required';
    }
    
    if (!formData.category) {
      errors.category = 'Category is required';
    }
    
    if (formData.requires_booking && formData.booking_fee && parseFloat(formData.booking_fee) < 0) {
      errors.booking_fee = 'Booking fee cannot be negative';
    }
    
    if (formData.discount_percentage) {
      const discount = parseFloat(formData.discount_percentage);
      if (discount < 0 || discount > 100) {
        errors.discount_percentage = 'Discount must be between 0 and 100';
      }
    }
    
    if (formData.discount_valid_from && formData.discount_valid_to && 
        formData.discount_valid_from > formData.discount_valid_to) {
      errors.discount_valid_to = 'Valid to date must be after valid from date';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      location: '',
      capacity: '',
      operating_hours: '',
      maintenance_schedule: '',
      is_available: true,
      requires_booking: false,
      booking_fee: '',
      discount_percentage: '',
      discount_valid_from: '',
      discount_valid_to: '',
      image_url: '',
    });
    setIsEditMode(false);
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const handleOpenEdit = async (facility) => {
    setSelectedFacility(facility);
    setFormData({
      name: facility.name,
      category: facility.category,
      description: facility.description || '',
      location: facility.location || '',
      capacity: facility.capacity || '',
      operating_hours: facility.operating_hours || '',
      maintenance_schedule: facility.maintenance_schedule || '',
      is_available: facility.is_available === true,
      requires_booking: facility.requires_booking === true,
      booking_fee: facility.booking_fee || '',
      discount_percentage: facility.discount_percentage || '',
      discount_valid_from: facility.discount_valid_from || '',
      discount_valid_to: facility.discount_valid_to || '',
      image_url: facility.image_url || '',
    });
    setIsEditMode(true);
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const handleOpenDetails = async (facility) => {
    setSelectedFacility(facility);
    setDetailsDialogOpen(true);
  };

  const handleOpenDelete = (facility) => {
    setSelectedFacility(facility);
    setDeleteDialogOpen(true);
  };

  const handleSubmitForm = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let result;
      if (isEditMode) {
        result = await api.updateFacility(selectedFacility.id, formData);
      } else {
        result = await api.createFacility(formData);
      }

      if (result.success) {
        showSnackbar(isEditMode ? 'Facility updated successfully' : 'Facility created successfully');
        setFormDialogOpen(false);
        await loadData();
        await loadCategories();
      } else {
        showSnackbar(result.error || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showSnackbar('Failed to save facility', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const result = await api.deleteFacility(selectedFacility.id);
      if (result.success) {
        showSnackbar('Facility deleted successfully');
        setDeleteDialogOpen(false);
        await loadData();
        await loadCategories();
      } else {
        showSnackbar(result.error || 'Failed to delete facility', 'error');
      }
    } catch (error) {
      console.error('Error deleting facility:', error);
      showSnackbar('Failed to delete facility', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (facility) => {
    setLoading(true);
    try {
      const newStatus = !facility.is_available;
      const result = await api.updateStatus(facility.id, newStatus);
      if (result.success) {
        showSnackbar(`Facility ${newStatus ? 'activated' : 'deactivated'} successfully`);
        await loadData();
      } else {
        showSnackbar(result.error || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showSnackbar('Failed to update status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = filteredFacilities.map(f => f.id);
      setSelectedIds(newSelected);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedIds, id];
    } else if (selectedIndex === 0) {
      newSelected = selectedIds.slice(1);
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = selectedIds.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = [
        ...selectedIds.slice(0, selectedIndex),
        ...selectedIds.slice(selectedIndex + 1)
      ];
    }

    setSelectedIds(newSelected);
  };

  const handleBulkDiscount = () => {
    if (selectedIds.length === 0) {
      showSnackbar('Please select at least one facility', 'warning');
      return;
    }
    setBulkDiscountData({
      discount_percentage: '',
      discount_valid_from: '',
      discount_valid_to: '',
    });
    setBulkDiscountDialogOpen(true);
  };

  const handleSubmitBulkDiscount = async () => {
    if (!bulkDiscountData.discount_percentage) {
      showSnackbar('Please enter discount percentage', 'warning');
      return;
    }

    setLoading(true);
    try {
      const result = await api.bulkUpdateDiscount(selectedIds, bulkDiscountData);
      if (result.success) {
        showSnackbar(result.message);
        setBulkDiscountDialogOpen(false);
        setSelectedIds([]);
        await loadData();
      } else {
        showSnackbar(result.error || 'Failed to apply discounts', 'error');
      }
    } catch (error) {
      console.error('Error applying bulk discount:', error);
      showSnackbar('Failed to apply discounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Statistics calculation - FIXED
  const getStatistics = () => {
    const total = facilities.length;
    const available = facilities.filter(f => f.is_available === true).length;
    const requiresBooking = facilities.filter(f => f.requires_booking === true).length;
    const hasDiscount = facilities.filter(f => f.discount_active === true).length;
    const totalRevenue = facilities.reduce((sum, f) => sum + (f.booking_fee || 0), 0);
    const totalDiscounts = facilities.reduce((sum, f) => sum + (f.savings || 0), 0);

    return { total, available, requiresBooking, hasDiscount, totalRevenue, totalDiscounts };
  };

  const stats = getStatistics();

  // Status chip colors - FIXED
  const getStatusColor = (status) => {
    return status === true ? 'success' : 'error';
  };

  const getDiscountStatus = (facility) => {
    if (facility.discount_active === true) return { label: 'Active', color: 'success' };
    if (facility.discount_percentage > 0) return { label: 'Expired', color: 'warning' };
    return { label: 'None', color: 'default' };
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Facility Management
                </Typography>
                <Typography variant="subtitle1">
                  Manage hotel facilities, amenities, and services
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenCreate}
                  sx={{ backgroundColor: 'white', color: 'primary.main', '&:hover': { backgroundColor: 'grey.100' } }}
                >
                  Add Facility
                </Button>
                {selectedIds.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Discount />}
                    onClick={handleBulkDiscount}
                    sx={{ backgroundColor: 'white', color: 'secondary.main', '&:hover': { backgroundColor: 'grey.100' } }}
                  >
                    Bulk Discount ({selectedIds.length})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadData}
                  disabled={loading}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Facilities
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {stats.total}
                      </Typography>
                    </Box>
                    <Category sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Available
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {stats.available}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Requires Booking
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {stats.requiresBooking}
                      </Typography>
                    </Box>
                    <Schedule sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Active Discounts
                      </Typography>
                      <Typography variant="h4" color="secondary.main">
                        {stats.hasDiscount}
                      </Typography>
                    </Box>
                    <LocalOffer sx={{ fontSize: 40, color: 'secondary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Value
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        ₦{stats.totalRevenue.toLocaleString()}
                      </Typography>
                    </Box>
                    <AttachMoney sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search Bar */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search facilities by name, description, or location..."
              value={filters.search}
              onChange={handleFilterChange('search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>

          {/* Active Filters */}
          {(filters.category || filters.status || filters.requiresBooking !== '' || filters.hasDiscount) && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Active Filters:</Typography>
                <Button size="small" onClick={clearFilters} startIcon={<ClearAll />}>
                  Clear All
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {filters.category && (
                  <Chip 
                    label={`Category: ${categoryNames[filters.category]}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('category')({ target: { value: '' } })}
                  />
                )}
                {filters.status && (
                  <Chip 
                    label={`Status: ${filters.status === 'available' ? 'Available' : 'Unavailable'}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('status')({ target: { value: '' } })}
                  />
                )}
                {filters.requiresBooking !== '' && (
                  <Chip 
                    label={`Booking Required: ${filters.requiresBooking === 'true' ? 'Yes' : 'No'}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('requiresBooking')({ target: { value: '' } })}
                  />
                )}
                {filters.hasDiscount && (
                  <Chip 
                    label={`Discount: ${filters.hasDiscount === 'active' ? 'Active' : filters.hasDiscount === 'inactive' ? 'Inactive' : 'None'}`} 
                    size="small" 
                    onDelete={() => handleFilterChange('hasDiscount')({ target: { value: '' } })}
                  />
                )}
              </Stack>
            </Paper>
          )}

          {/* Facilities Table */}
          <Paper elevation={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedIds.length > 0 && selectedIds.length < filteredFacilities.length}
                            checked={filteredFacilities.length > 0 && selectedIds.length === filteredFacilities.length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Facility</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Capacity</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Fee</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Discount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Booking Required</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFacilities
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((facility) => (
                          <TableRow key={facility.id} hover selected={selectedIds.includes(facility.id)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedIds.includes(facility.id)}
                                onChange={() => handleSelectOne(facility.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                  {categoryIcons[facility.category]}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" fontWeight="medium">
                                    {facility.name}
                                  </Typography>
                                  {facility.description && (
                                    <Typography variant="caption" color="textSecondary">
                                      {facility.description.substring(0, 50)}...
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={categoryIcons[facility.category]}
                                label={categoryNames[facility.category]} 
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {facility.location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOn fontSize="small" color="action" />
                                  <Typography variant="body2">{facility.location}</Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {facility.capacity && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <People fontSize="small" color="action" />
                                  <Typography variant="body2">{facility.capacity}</Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {facility.booking_fee > 0 ? (
                                <Box>
                                  <Typography variant="body2" fontWeight="bold" color="primary">
                                    ₦{facility.booking_fee.toLocaleString()}
                                  </Typography>
                                  {facility.discount_active && (
                                    <Typography variant="caption" color="success.main">
                                      ₦{facility.final_price.toLocaleString()} after discount
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="textSecondary">Free</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {facility.discount_percentage > 0 ? (
                                <Tooltip title={facility.discount_active ? 'Active Discount' : 'Discount (Expired/Upcoming)'}>
                                  <Chip
                                    icon={<Percent />}
                                    label={`${facility.discount_percentage}% OFF`}
                                    size="small"
                                    color={getDiscountStatus(facility).color}
                                  />
                                </Tooltip>
                              ) : (
                                <Chip label="No Discount" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={facility.is_available ? 'Available' : 'Unavailable'} 
                                color={getStatusColor(facility.is_available)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={facility.requires_booking ? 'Required' : 'Not Required'} 
                                color={facility.requires_booking ? 'warning' : 'default'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="View Details">
                                  <IconButton 
                                    size="small" 
                                    color="info"
                                    onClick={() => handleOpenDetails(facility)}
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleOpenEdit(facility)}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={facility.is_available ? 'Deactivate' : 'Activate'}>
                                  <IconButton 
                                    size="small" 
                                    color={facility.is_available ? 'warning' : 'success'}
                                    onClick={() => handleToggleStatus(facility)}
                                  >
                                    {facility.is_available ? <Block /> : <CheckCircle />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleOpenDelete(facility)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredFacilities.length === 0 && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                      No facilities found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {Object.values(filters).some(f => f) 
                        ? 'Try adjusting your filters' 
                        : 'Click "Add Facility" to create your first facility'
                      }
                    </Typography>
                  </Box>
                )}

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={filteredFacilities.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6">Filter Facilities</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={handleFilterChange('category')}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {Object.entries(categoryNames).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="unavailable">Unavailable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Booking Required</InputLabel>
                <Select
                  value={filters.requiresBooking}
                  onChange={handleFilterChange('requiresBooking')}
                  label="Booking Required"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Required</MenuItem>
                  <MenuItem value="false">Not Required</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Discount Status</InputLabel>
                <Select
                  value={filters.hasDiscount}
                  onChange={handleFilterChange('hasDiscount')}
                  label="Discount Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active Discounts</MenuItem>
                  <MenuItem value="inactive">Inactive/Expired Discounts</MenuItem>
                  <MenuItem value="none">No Discounts</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => setFilterDialogOpen(false)}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facility Form Dialog (Create/Edit) */}
      <Dialog 
        open={formDialogOpen} 
        onClose={() => setFormDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            {isEditMode ? 'Edit Facility' : 'Add New Facility'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Facility Name"
                value={formData.name}
                onChange={handleFormChange('name')}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!formErrors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleFormChange('category')}
                  label="Category"
                >
                  {Object.entries(categoryNames).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {categoryIcons[value]}
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={handleFormChange('description')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={handleFormChange('location')}
                placeholder="e.g., Ground Floor, East Wing"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Capacity"
                value={formData.capacity}
                onChange={handleFormChange('capacity')}
                InputProps={{ endAdornment: <Typography>people</Typography> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Operating Hours"
                value={formData.operating_hours}
                onChange={handleFormChange('operating_hours')}
                placeholder="e.g., 6:00 AM - 10:00 PM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maintenance Schedule"
                value={formData.maintenance_schedule}
                onChange={handleFormChange('maintenance_schedule')}
                placeholder="e.g., Weekly cleaning every Monday"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider>Pricing & Booking</Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_booking}
                    onChange={handleFormChange('requires_booking')}
                    color="primary"
                  />
                }
                label="Requires Booking"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Booking Fee (₦)"
                value={formData.booking_fee}
                onChange={handleFormChange('booking_fee')}
                disabled={!formData.requires_booking}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider>Discount Settings</Divider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Discount Percentage (%)"
                value={formData.discount_percentage}
                onChange={handleFormChange('discount_percentage')}
                error={!!formErrors.discount_percentage}
                helperText={formErrors.discount_percentage}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Valid From"
                value={formData.discount_valid_from}
                onChange={handleFormChange('discount_valid_from')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Valid To"
                value={formData.discount_valid_to}
                onChange={handleFormChange('discount_valid_to')}
                error={!!formErrors.discount_valid_to}
                helperText={formErrors.discount_valid_to}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider>Status</Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_available}
                    onChange={handleFormChange('is_available')}
                    color="success"
                  />
                }
                label="Available for Use"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitForm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facility Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Facility Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedFacility && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ width: 60, height: 60, bgcolor: theme.palette.primary.main }}>
                    {categoryIcons[selectedFacility.category]}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {selectedFacility.name}
                    </Typography>
                    <Chip 
                      icon={categoryIcons[selectedFacility.category]}
                      label={categoryNames[selectedFacility.category]} 
                      size="small"
                    />
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedFacility.description || 'No description provided'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Location</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedFacility.location || 'Not specified'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Capacity</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedFacility.capacity ? `${selectedFacility.capacity} people` : 'Not specified'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Operating Hours</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedFacility.operating_hours || 'Not specified'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Maintenance Schedule</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedFacility.maintenance_schedule || 'Not specified'}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider>Pricing & Booking</Divider>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Booking Fee</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {selectedFacility.booking_fee > 0 ? `₦${selectedFacility.booking_fee.toLocaleString()}` : 'Free'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Requires Booking</Typography>
                <Chip 
                  label={selectedFacility.requires_booking ? 'Yes' : 'No'} 
                  color={selectedFacility.requires_booking ? 'warning' : 'default'}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Chip 
                  label={selectedFacility.is_available ? 'Available' : 'Unavailable'} 
                  color={getStatusColor(selectedFacility.is_available)}
                  size="small"
                />
              </Grid>
              
              {selectedFacility.discount_percentage > 0 && (
                <>
                  <Grid item xs={12}>
                    <Divider>Discount Information</Divider>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="textSecondary">Discount</Typography>
                    <Typography variant="body1" color="secondary" fontWeight="bold">
                      {selectedFacility.discount_percentage}% OFF
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="textSecondary">Discounted Price</Typography>
                    <Typography variant="body1" color="success.main" fontWeight="bold">
                      ₦{selectedFacility.final_price.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="textSecondary">Valid Period</Typography>
                    <Typography variant="body2">
                      {selectedFacility.discount_valid_from || 'Immediate'} - {selectedFacility.discount_valid_to || 'Ongoing'}
                    </Typography>
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <Divider>Audit Information</Divider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Created By</Typography>
                <Typography variant="body2">
                  {selectedFacility.created_by_name || 'System'} on {new Date(selectedFacility.created_at).toLocaleString()}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Last Updated</Typography>
                <Typography variant="body2">
                  {new Date(selectedFacility.updated_at).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {selectedFacility && (
            <Button 
              variant="contained" 
              onClick={() => {
                setDetailsDialogOpen(false);
                handleOpenEdit(selectedFacility);
              }}
            >
              Edit Facility
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedFacility?.name}"? This action cannot be undone.
          </Typography>
          {selectedFacility?.booking_fee > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This facility has a booking fee of ₦{selectedFacility.booking_fee.toLocaleString()}.
              Deleting it may affect existing bookings.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Discount Dialog */}
      <Dialog 
        open={bulkDiscountDialogOpen} 
        onClose={() => setBulkDiscountDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Discount color="secondary" />
            <Typography variant="h6">Apply Bulk Discount</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Applying discount to {selectedIds.length} selected facility(s)
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="number"
                label="Discount Percentage (%)"
                value={bulkDiscountData.discount_percentage}
                onChange={(e) => setBulkDiscountData(prev => ({ ...prev, discount_percentage: e.target.value }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Valid From"
                value={bulkDiscountData.discount_valid_from}
                onChange={(e) => setBulkDiscountData(prev => ({ ...prev, discount_valid_from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Valid To"
                value={bulkDiscountData.discount_valid_to}
                onChange={(e) => setBulkDiscountData(prev => ({ ...prev, discount_valid_to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDiscountDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleSubmitBulkDiscount}
            disabled={loading}
          >
            Apply Discount
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilityManagement;